import { useEffect, useRef } from 'react';
import { LoggerService, PubSubService } from '@vivli/shared/infrastructure/service';
import { useAppConfigContext } from '@vivli/core/infrastructure/context';
import { IChatApiMessage } from '@vivli/features/chat/infrastructure/interface';

const CHAT_TOPIC_PREFIX = 'chat:';
const CHAT_MESSAGE_EVENT = 'receiveChatMessage';

// DEV-669: subscribes to the Ably broadcast for a chat channel so a message posted through any Vivli
// API (AI reply, another user, or an automated/system message) appears in an already-open chat window
// without a refresh. Mirrors use-dr-pub-sub.hook.ts. The backend publishes a JSON ChatBrokeredMessage
// to the "chat:{channelId}" topic under the "receiveChatMessage" event.
export const useChatPubSub = (channelId: string, onChatMessage: (message: IChatApiMessage) => void) => {
    const config = useAppConfigContext();
    const pubSubService = useRef<PubSubService>(new PubSubService(config.environmentName, config.apiUri));
    const isSubscribed = useRef<boolean>(false);

    // Keep the latest callback in a ref so a new handler each render doesn't force a resubscribe.
    const callbackRef = useRef(onChatMessage);
    callbackRef.current = onChatMessage;

    const topicName = () => `${CHAT_TOPIC_PREFIX}${channelId}`;

    const handleMessage = (message: any) => {
        if (!message?.data) {
            return;
        }

        try {
            const parsed: IChatApiMessage = typeof message.data === 'string' ? JSON.parse(message.data) : message.data;
            callbackRef.current?.(parsed);
        } catch (e) {
            LoggerService.error('Failed to parse incoming chat message from Ably.', e);
        }
    };

    useEffect(() => {
        if (!channelId) {
            return;
        }

        if (!isSubscribed.current) {
            pubSubService.current?.SubscribeToEvent(topicName(), CHAT_MESSAGE_EVENT, handleMessage);
            isSubscribed.current = true;
        }

        return () => {
            isSubscribed.current = false;
            pubSubService.current?.UnsubscribeFromChannel(topicName());
        };
    }, [channelId]);

    useEffect(() => {
        return () => {
            pubSubService.current?.CloseConnection();
        };
    }, []);
};
